import React, { useRef, useState } from 'react';
import { Camera, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fileToDataUrl } from '@/services/transportApi';
import { compressImageFile } from '@/utils/imageCompression';
import { InAppCamera } from '@/components/transport/InAppCamera';

// Shrink phone photos (often 3-10 MB) before upload.
const toSmallDataUrl = async (file, maxEdge = 1280) => {
  let f = file;
  try { f = await compressImageFile(file, { maxEdge, quality: 0.7, sizeThresholdBytes: 250 * 1024 }); } catch { /* keep original */ }
  return fileToDataUrl(f);
};

const FallbackLink = ({ onClick, testId }) => (
  <button type="button" onClick={onClick} className="text-xs text-gray-500 underline underline-offset-2 hover:text-gray-700" data-testid={testId}>
    Use phone camera / gallery
  </button>
);

/**
 * Photo capture for transport forms. Opens the IN-APP camera (no switch to the
 * phone's camera app, which low-memory phones kill the browser for). A small
 * "Use phone camera / gallery" link keeps the old file picker as a backup.
 */
export const CapturePhoto = ({ label = 'Take Photo', onCapture, testId = 'capture-photo', busy = false, captured = false, maxEdge = 1280 }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [cam, setCam] = useState(false);

  const deliver = (dataUrl) => { setCam(false); setPreview(dataUrl); onCapture?.(dataUrl); };
  const pickFile = () => { setCam(false); inputRef.current?.click(); };
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) deliver(await toSmallDataUrl(file, maxEdge));
  };

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
      {cam ? (
        <InAppCamera onShot={deliver} onCancel={() => setCam(false)} onFallback={pickFile} maxEdge={maxEdge} testId={`${testId}-camera`} />
      ) : (
        <>
          <Button type="button" variant="outline" className="w-full min-h-12 h-auto py-2 whitespace-normal text-center border-amber-300"
            onClick={() => setCam(true)} disabled={busy} data-testid={`${testId}-btn`}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
              captured ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <Camera className="w-4 h-4 mr-2" />}
            {captured ? 'Photo captured — retake?' : label}
          </Button>
          <FallbackLink onClick={pickFile} testId={`${testId}-fallback`} />
        </>
      )}
      {preview && !cam && (
        <img src={preview} alt="capture" className="w-full max-h-48 object-contain rounded-lg border"
          data-testid={`${testId}-preview`} />
      )}
    </div>
  );
};

/**
 * Multiple-photo capture (fuel bill + transaction slip, 2-3 page service bill).
 * Each in-app shot appends a page; pages can be removed. onChange gets the array.
 */
export const MultiCapturePhoto = ({ label = 'Add Photo', photos = [], onChange, testId = 'multi-photo', max = 6, maxEdge = 1600 }) => {
  const inputRef = useRef(null);
  const [cam, setCam] = useState(false);
  const add = (urls) => onChange?.([...photos, ...urls].slice(0, max));
  const pickFile = () => { setCam(false); inputRef.current?.click(); };
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const urls = [];
    for (const f of files) urls.push(await toSmallDataUrl(f, maxEdge));
    add(urls);
  };
  const remove = (i) => onChange?.(photos.filter((_, idx) => idx !== i));
  const full = photos.length >= max;
  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" accept="image/*" multiple
        className="hidden" onChange={handleFiles} data-testid={`${testId}-input`} />
      {cam ? (
        <InAppCamera onShot={(u) => { setCam(false); add([u]); }} onCancel={() => setCam(false)} onFallback={pickFile} maxEdge={maxEdge} quality={0.75} testId={`${testId}-camera`} />
      ) : (
        <>
          <Button type="button" variant="outline" className="w-full min-h-12 h-auto py-2 whitespace-normal text-center border-amber-300"
            onClick={() => setCam(true)} disabled={full} data-testid={`${testId}-btn`}>
            <Camera className="w-4 h-4 mr-2" />
            {photos.length ? `Add another page (${photos.length}/${max})` : label}
          </Button>
          {!full && <FallbackLink onClick={pickFile} testId={`${testId}-fallback`} />}
        </>
      )}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid={`${testId}-previews`}>
          {photos.map((p, i) => (
            <div key={i} className="relative">
              <img src={p} alt={`page ${i + 1}`} className="w-full h-20 object-cover rounded border" data-testid={`${testId}-preview-${i}`} />
              <button type="button" onClick={() => remove(i)}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none"
                data-testid={`${testId}-remove-${i}`}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CapturePhoto;
